
import { CrudCollection } from "../../CrudCollection";
import { filterSetApi } from "../../filters/filterSetApi";
import { readCollectionQueryState } from "../../filters/collectionQueryState";
import { FilterSetItem } from "./filterSetItem";

export default {
  components: {
    FilterSetItem,
  },
  props: {
    collection: {
      type: CrudCollection,
      required: true,
    },
    scope: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      sets: [],
      loaded: false,
      loading: false,
      saving: false,
      menuOpen: false,
      dialogOpen: false,
      editingSet: null,
      name: "",
      isGlobal: false,
      errorMessage: "",
      dialogError: "",
      // Derived from the list response, not a prop pinned to true
      // (asana-995, Task 10) -- the API is the source of truth for whether
      // this caller may manage a Shared set.
      canManageGlobal: false,
    };
  },
  computed: {
    mine() {
      return this.sets.filter((set) => set.is_mine);
    },
    shared() {
      return this.sets.filter((set) => set.is_global);
    },
    currentQueryState() {
      return readCollectionQueryState(this.collection);
    },
    hasCurrentState() {
      return this.collection.hasMeaningfulQueryState;
    },
  },
  watch: {
    menuOpen(open) {
      if (open && !this.loaded) this.loadSets();
    },
    scope() {
      this.sets = [];
      this.loaded = false;
    },
  },
  methods: {
    async loadSets() {
      this.loading = true;
      this.errorMessage = "";

      try {
        const response = await filterSetApi.list(this.scope);
        this.sets = response.data;
        this.canManageGlobal = response.can_manage_global;
        this.loaded = true;
      } catch (error) {
        this.errorMessage = this.errorFromResponse(
          error,
          "Saved filters could not be loaded."
        );
      } finally {
        this.loading = false;
      }
    },
    loadSet(set) {
      this.$emit("load", set.query);
      this.menuOpen = false;
    },
    openCreateDialog() {
      if (!this.hasCurrentState) return;

      this.menuOpen = false;
      this.editingSet = null;
      this.name = "";
      this.isGlobal = false;
      this.dialogError = "";
      this.dialogOpen = true;
    },
    openRenameDialog(set) {
      this.menuOpen = false;
      this.editingSet = set;
      this.name = set.name;
      this.isGlobal = set.is_global;
      this.dialogError = "";
      this.dialogOpen = true;
    },
    closeDialog() {
      if (this.saving) return;
      this.dialogOpen = false;
    },
    async submitDialog() {
      const name = this.name.trim();
      if (!name || (!this.editingSet && !this.hasCurrentState)) return;

      this.saving = true;
      this.dialogError = "";

      try {
        let savedSet;
        if (this.editingSet) {
          savedSet = await filterSetApi.update(this.editingSet.id, { name });
          this.sets = this.sets.map((set) =>
            set.id === savedSet.id ? savedSet : set
          );
        } else {
          savedSet = await filterSetApi.create({
            scope: this.scope,
            name,
            query: this.currentQueryState,
            is_global: this.isGlobal,
          });
          this.sets = [...this.sets, savedSet];
        }

        this.dialogOpen = false;
        this.loaded = true;
      } catch (error) {
        this.dialogError = this.errorFromResponse(
          error,
          "Saved filters could not be updated."
        );
      } finally {
        this.saving = false;
      }
    },
    async overwriteSet(set) {
      if (!this.hasCurrentState) return;

      this.errorMessage = "";
      try {
        await filterSetApi.update(set.id, {
          query: this.currentQueryState,
        });
        this.loaded = false;
        await this.loadSets();
      } catch (error) {
        this.errorMessage = this.errorFromResponse(
          error,
          "Saved filters could not be overwritten."
        );
      }
    },
    async deleteSet(set) {
      if (!window.confirm(`Delete "${set.name}"?`)) return;

      this.errorMessage = "";
      try {
        await filterSetApi.remove(set.id);
        this.sets = this.sets.filter(({ id }) => id !== set.id);
      } catch (error) {
        this.errorMessage = this.errorFromResponse(
          error,
          "Saved filters could not be deleted."
        );
      }
    },
    errorFromResponse(error, fallback) {
      const response = error && (error.response || error);
      const data = response && (response.data || response);
      const errors = data && data.errors;

      if (errors) {
        const firstError = Object.values(errors)[0];
        if (Array.isArray(firstError)) return firstError[0];
        if (firstError) return firstError;
      }

      return (data && data.message) || fallback;
    },
  },
};
